import get from 'lodash/get';
import snakeCase from 'lodash/snakeCase';
import fromPairs from 'lodash/fromPairs';
import join from 'lodash/join';
import { push } from 'react-router-redux';
import jwt from 'jsonwebtoken';
import Moment from 'moment';

import {
  API_AUTH_CREDENTIALS_URL,
  API_AUTH_LOGIN_URL,
  API_AUTH_TOKEN_URL,
  API_RESET_CREDENTIALS_REQUEST,
  API_UPDATE_CREDENTIALS,
  API_USERS_URL,
  API_USER_PASSPHRASE_URL,
  API_USER_DOWNLOADS_URL,
  API_USER_DOWNLOAD_URL,
  API_USER_FILE_URL,
  API_ACCOUNTS_URL,
  API_ACCOUNT_CASH_BALANCE_URL,
  API_ACCOUNT_POSITIONS_URL,
  API_ACCOUNT_PERFORMANCE_RAW_DATA_URL,
  API_ACCOUNT_PERFORMANCE_SUMMARY_URL,
  API_ACCOUNT_TRANSACTIONS_URL,
  API_ACCOUNT_ORDERS_URL,
  API_ACCOUNT_CASH_TRANSACTIONS_URL,
  API_ACCOUNT_DEPOSITS_URL,
  API_ACCOUNT_WITHDRAWALS_URL,
  API_ACCOUNT_INSTRUCTIONS_WIRE_URL,
  API_ACCOUNT_INSTRUCTIONS_CHECK_URL,
  API_ACCOUNT_RESTRICTIONS_URL,
  API_ACCOUNT_DOWNLOADS_URL,
  API_ACCOUNT_DOWNLOAD_URL,
  API_OFFERINGS_URL,
  API_OFFERING_URL,
  API_OFFERING_DOCUMENTS_URL,
  API_PLAID_LINK_SETTINGS_URL,
  API_QUOTES_CHARTDATA_URL,
  API_QUOTES_SNAPQUOTES_URL,
  API_QUOTES_ENHANCEDSNAPQUOTES_URL,
  API_QUOTES_PRIVATE_URL,
  API_VERIFY_EMAIL_URL,
  API_BASE_URL,
  API_LOCAL_ACCOUNT_RESTRICTIONS_URL,
  replacePlaceholders,
} from './urls';
import callWebApi from './callWebApi';

import { addNotification } from './notifications';
import { fetchOfferingOrders } from './investment';
import Raven from '../sentry';
import isTradablePosition from '../components/Portfolio/isTradablePosition';
import {
  prepJSONforPrivGrid,
  prepJSONforSecuritiesGrid,
} from '../components/Portfolio/prepJsonForGrids';

const debug = require('debug')('cambria:actions');
const Big = require('big.js/big.js');

export const LOGOUT = 'LOGOUT';

export const AUTH_TOKEN = 'AUTH_TOKEN';
export const AUTH_TOKEN_USER = 'AUTH_TOKEN_USER';
export const AUTH_CREDENTIALS_CREATING = 'AUTH_CREDENTIALS_CREATING';
export const AUTH_CREDENTIALS_CREATED = 'AUTH_CREDENTIALS_CREATED';

export const CURRENT_ACCOUNT = 'CURRENT_ACCOUNT';

export const SIGN_UP_PERSONAL_INFO = 'SIGN_UP_PERSONAL_INFO';
export const SIGN_UP_INVESTMENT_INFO = 'SIGN_UP_INVESTMENT_INFO';
export const SIGN_UP_PLAID_INFO = 'SIGN_UP_PLAID_INFO';
export const SIGN_UP_SIGNATURE = 'SIGN_UP_SIGNATURE';
export const SIGN_UP_BACKUP_WITHHOLDING = 'SIGN_UP_BACKUP_WITHHOLDING';
export const SIGN_UP_PROCESSING = 'SIGN_UP_PROCESSING';
export const SIGN_UP_COMPLETED = 'SIGN_UP_COMPLETED';

export const TRANSFER_FUNDS_INFO = 'TRANSFER_FUNDS_INFO';
export const TRANSFER_FUNDS_DEPOSIT_PROCESSING = 'TRANSFER_FUNDS_DEPOSIT_PROCESSING';
export const TRANSFER_FUNDS_WITHDRAWAL_PROCESSING = 'TRANSFER_FUNDS_WITHDRAWAL_PROCESSING';
export const TRANSFER_FUNDS_COMPLETED = 'TRANSFER_FUNDS_COMPLETED';

export const TRADE_SECURITY_INFO = 'TRADE_SECURITY_INFO';
export const TRADE_SECURITY_TRANSFER_INFO = 'TRADE_SECURITY_TRANSFER_INFO';
export const TRADE_SECURITY_PROCESSING = 'TRADE_SECURITY_PROCESSING';
export const TRADE_SECURITY_COMPLETED = 'TRADE_SECURITY_COMPLETED';

export const SERVICE_STATUS_CHECK_REQUESTED = 'SERVICE_STATUS_CHECK/REQUESTED';
export const SERVICE_STATUS_CHECK_SUCCEED = 'SERVICE_STATUS_CHECK/SUCCEED';
export const SERVICE_STATUS_CHECK_FAILED = 'SERVICE_STATUS_CHECK/FAILED';

export const ACCOUNT_VALUE_REQUESTED = 'ACCOUNT_VALUE_REQUESTED';
export const ACCOUNT_VALUE_COMPLETED = 'ACCOUNT_VALUE_COMPLETED';
export const SET_ACCREDITATION = 'SET_ACCREDITATION';

export const HISTORY_FILTER_TOGGLE = 'HISTORY_FILTER_TOGGLE';
export const HISTORY_FILTER_RESET = 'HISTORY_FILTER_RESET';

export const TRANSFER_CANCELED = 'TRANSFER_CANCELED';

export const FETCH_CACHE_DURATION = 60 * 1000;

export const setAccreditation = isAccredited => (dispatch) => {
  dispatch({ type: SET_ACCREDITATION, isAccredited });
};

export const logout = () => ({ type: LOGOUT });


export const createFetchActionCreator = (
  urlStrOrFunc,
  identifier,
  extraPayload = {},
  {
    force = false,
    requireAuth = true,
    transformData,
  } = {},
) => ({ cacheDuration = FETCH_CACHE_DURATION } = {}) => (
  async (dispatch, getState) => {
    const state = getState();

    const url = (typeof urlStrOrFunc === 'function') ? urlStrOrFunc(state) : urlStrOrFunc;

    if (requireAuth && !state.authToken) {
      debug(`Tried to call fetch on ${url} without authToken in state`);
      return null;
    }

    const currentState = extraPayload.accountNumber ? (
      state[`${identifier}ByAccount`] && state[`${identifier}ByAccount`][extraPayload.accountNumber]
    ) : (
      state[identifier]
    );

    if (currentState && currentState.isFetching &&
      (new Date() - currentState.isFetching) < cacheDuration) {
      return null;
    }

    if (!force && (currentState && (new Date() - currentState.fetchedAt) < cacheDuration)) {
      return currentState.data;
    }

    const ACTION_TYPE = snakeCase(identifier).toUpperCase();

    debug('Fetching %s: %s', identifier, url);
    dispatch({ type: `${ACTION_TYPE}/REQUESTED`, payload: { ...extraPayload } });

    try {
      const res = await callWebApi('GET', url, null, state.authToken);
      const data = get(res, identifier);
      debug('Fetched %s: %o', identifier, data);
      dispatch({
        type: `${ACTION_TYPE}/SUCCEED`,
        payload: {
          ...extraPayload,
          isFetching: false,
          fetchedAt: new Date(),
          data: (transformData ? transformData(data) : data),
        },
      });
      return data;
    } catch (err) {
      debug('Fetch failed %s: %s %s', identifier, url, err);
      dispatch({ type: `${ACTION_TYPE}/FAILED`, payload: { ...extraPayload, error: err } });
      dispatch(addNotification(`${err.toString()}`));
      if (err.res) {
        const { error } = await err.res.json();
        if (error.message === 'review') { // if account marked as suspicious for several failed attempts
          dispatch(logout());
          dispatch(addNotification('Your account is under review due to suspicious activity in another browser session.' +
            ' Please check your email inbox for further details on how to unlock your account.'));
        }
        if (error.message === 'inactive') {
          dispatch(logout());
          dispatch(addNotification('Your account is locked due to suspicious activity. Please check your email inbox' +
            ' for further details on how to unlock your account.'));
        }
      }
      if (Raven) {
        Raven.captureException(err);
      }
    }
    return null;
  }
);


// allow the possibility for handling multiple accounts
// accounts is currently an array so this prevents having
// to grab the first one every time you need the account
export const setCurrentAccount = accountId => (dispatch) => {
  dispatch({ type: 'CURRENT_ACCOUNT', accountId });
};


export const fetchAccounts = () => async (dispatch) => {
  const fn = createFetchActionCreator(state => `${API_ACCOUNTS_URL}?userId=${state.authTokenUser.id}`, 'accounts', {}, {
    force: true,
  });
  const accounts = await dispatch(fn());
  if (accounts.length) {
    dispatch(setCurrentAccount(accounts[0].accountNumber));
  }
};


export const fetchAccountCashBalance = (accountNumber, force = false) =>
  async (dispatch, getState) => {
    const url = replacePlaceholders(API_ACCOUNT_CASH_BALANCE_URL, { accountNumber });
    const fn = createFetchActionCreator(url, 'accountCashBalance', { accountNumber }, { force })();
    return fn(dispatch, getState);
  };

export const fetchAccountCashTransactions = (accountNumber, dateRangeCode = '5y', force = false) =>
  async (dispatch, getState) => {
    const url = replacePlaceholders(API_ACCOUNT_CASH_TRANSACTIONS_URL, {
      accountNumber,
      dateRangeCode,
    });
    const fn = createFetchActionCreator(url, 'accountCashTransactions', { accountNumber }, { force })();
    return fn(dispatch, getState);
  };

/*
 * Fetches account cash balance and adjusts the balance total from the pending
 * offering orders and pending withdrawals
 */
export const fetchAccountCashBalanceWithOfferingOrders = accountNumber => async (
  dispatch,
  getState,
) => {
  const state = getState();
  const currentOfferingOrdersState = state.offeringOrdersByAccount &&
    state.offeringOrdersByAccount[accountNumber];
  const currentAccountCashTransactionsState = state.accountCashTransactionsByAccount &&
    state.accountCashTransactionsByAccount[accountNumber];
  if ((currentOfferingOrdersState && currentOfferingOrdersState.isFetching) ||
  (currentAccountCashTransactionsState && currentAccountCashTransactionsState.isFetching)) {
    return null;
  }

  const result = await Promise.all([
    dispatch(fetchOfferingOrders(accountNumber)),
    dispatch(fetchAccountCashTransactions(accountNumber, '5y', true)),
  ]);
  const offeringOrders = result[0];
  const cashTransactions = result[1];

  const transformData = (data) => {
    const totalComittedToOfferingOrders = offeringOrders.reduce((total, order) => (
      order.status === 'created' ? total + order.requestedTotalCurrencyAmt : total
    ), 0);

    const pendingWithdrawals = cashTransactions.reduce((total, trn) => (
      (trn.status === 'PD' || trn.status === 'PR') && trn.transactionType === 'AHW' ? total + Number.parseFloat(trn.amount) : total
    ), 0);

    const pendingDeposits = cashTransactions.reduce((total, trn) => (
      (trn.status === 'PD' || trn.status === 'PR') && trn.transactionType === 'AHD' ? total + Number.parseFloat(trn.amount) : total
    ), 0);

    const balanceAvailableForT1Trades = Math.max(
      0,
      Number.parseFloat(Big(data.balanceAvailableForT1Trades)
        .minus(totalComittedToOfferingOrders).minus(pendingWithdrawals)),
    );

    const balanceWithheldFromT1Trades = (
      Number.parseFloat(data.balanceWithheldFromT1Trades) + totalComittedToOfferingOrders
    );

    const pendingTransfersAvailableForOfferings = Math.min(pendingDeposits, pendingDeposits +
      Math.min(0, Number.parseFloat(Big(data.balanceAvailableForT1Trades)
        .minus(pendingWithdrawals).minus(totalComittedToOfferingOrders))));

    return {
      ...data,
      balanceAvailableForT1Trades,
      balanceWithheldFromT1Trades,
      balanceBeforeCurrentActivity: Number.parseFloat(data.balanceAvailableForT1Trades),
      pendingTransfersAvailableForOfferings,
      pendingTransfers: pendingDeposits - pendingWithdrawals,
    };
  };

  const url = replacePlaceholders(API_ACCOUNT_CASH_BALANCE_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'accountCashBalance', { accountNumber }, { transformData, force: true })();
  return fn(dispatch, getState);
};


export const fetchAccountPositions = (accountNumber, force = false) =>
  async (dispatch, getState) => {
    const url = replacePlaceholders(API_ACCOUNT_POSITIONS_URL, { accountNumber });
    const fn = createFetchActionCreator(url, 'accountPositions', { accountNumber }, { force })();
    return fn(dispatch, getState);
  };


export const fetchAccountPerformanceRawData = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_ACCOUNT_PERFORMANCE_RAW_DATA_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'accountPerformanceRawData', { accountNumber })();
  return fn(dispatch, getState);
};


export const fetchAccountPerformanceSummary = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_ACCOUNT_PERFORMANCE_SUMMARY_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'accountPerformanceSummary', { accountNumber })();
  return fn(dispatch, getState);
};

export const fetchAccountTransactions = (accountNumber, dateRangeCode = '5y', force = false) =>
  async (dispatch, getState) => {
    const url = replacePlaceholders(API_ACCOUNT_TRANSACTIONS_URL, {
      accountNumber,
      dateRangeCode,
    });
    const fn = createFetchActionCreator(url, 'accountTransactions', { accountNumber }, { force })();
    return fn(dispatch, getState);
  };

export const fetchAccountOrders = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_ACCOUNT_ORDERS_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'accountOrders', { accountNumber })();
  return fn(dispatch, getState);
};
export const fetchAccountRestrictions = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_ACCOUNT_RESTRICTIONS_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'accountRestrictions', { accountNumber })();
  return fn(dispatch, getState);
};

export const fetchAccountLocalRestrictions = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_LOCAL_ACCOUNT_RESTRICTIONS_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'accountLocalRestrictions', { accountNumber })();
  return fn(dispatch, getState);
};


export const fetchDownloads = accountNum => async (dispatch, getState) => {
  const userId = getState().authTokenUser.id;
  const accountNumber = accountNum || getState().currentAccount;
  return Promise.all([
    createFetchActionCreator(
      replacePlaceholders(API_ACCOUNT_DOWNLOADS_URL, { accountNumber }),
      'accountDownloads',
      { accountNumber },
    )()(dispatch, getState),
    createFetchActionCreator(
      replacePlaceholders(API_USER_DOWNLOADS_URL, { userId }),
      'userDownloads',
      { userId },
    )()(dispatch, getState),
  ]);
};


export const createUserFileSignatureLink = authToken => async (dispatch, getState) => {
  const { authTokenUser } = getState();
  return replacePlaceholders(API_USER_FILE_URL, {
    userId: authTokenUser.id,
    authToken,
    userFileName: 'signature',
  });
};


export const createUserDownloadLink = (
  authToken,
  type,
  reportId,
  {
    download = false,
  } = {},
) => async (dispatch, getState) => {
  const { authTokenUser } = getState();
  return replacePlaceholders(API_USER_DOWNLOAD_URL, {
    userId: authTokenUser.id,
    authToken,
    type,
    reportId,
    download,
  });
};


export const createAccountDownloadLink = (
  authToken,
  type,
  year,
  {
    download = false,
  } = {},
) => async (dispatch, getState) => {
  const { currentAccount } = getState();
  return replacePlaceholders(API_ACCOUNT_DOWNLOAD_URL, {
    accountNumber: currentAccount,
    authToken,
    type,
    year,
    download,
  });
};

const getOfferingNextCloseDate = ({ closeDates, closeDate }) => {
  if (closeDates && closeDates.length) {
    const today = (new Date()).toISOString();
    // closeDates is a list of ISO 8601 string so a lexical sort will put
    // them in chronological order
    const sortedCloseDates = [...closeDates].sort();
    const nextCloseDate = sortedCloseDates.find(d => d > today);
    // Use the found close date or the last date in list
    return nextCloseDate || sortedCloseDates.slice(-1)[0];
  }
  return closeDate;
};

export const fetchOfferings = createFetchActionCreator(API_OFFERINGS_URL, 'offerings', {}, {
  requireAuth: false,
  transformData: offerings => offerings.map(offering => ({
    ...offering,
    closeDate: offering.recurring ? getOfferingNextCloseDate(offering) : offering.closeDate,
  })),
});

export const fetchOffering = offeringId => async (dispatch, getState) => {
  const url = replacePlaceholders(API_OFFERING_URL, { offeringId });
  const fn = createFetchActionCreator(url, 'offering', { offeringId }, {
    requireAuth: false,
    transformData: (offering) => {
      const { closeDate, ...rest } = offering;
      return {
        ...rest,
        closeDate: offering.recurring ? getOfferingNextCloseDate(offering) : offering.closeDate,
      };
    },
  })();
  return fn(dispatch, getState);
};


export const fetchOfferingDocuments = offeringId => (dispatch, getState) => {
  const url = replacePlaceholders(API_OFFERING_DOCUMENTS_URL, { offeringId });
  const fn = createFetchActionCreator(url, 'offeringDocuments', { offerId: offeringId }, { force: true, requireAuth: false })();
  return fn(dispatch, getState);
};
export const fetchUser = createFetchActionCreator(state => `${API_USERS_URL}/${state.authTokenUser.id}`, 'user', {}, { force: true });


export const setAuthToken = token => (dispatch) => {
  const { user } = jwt.decode(token) || {};

  if (user) {
    dispatch({ type: AUTH_TOKEN_USER, user });
    dispatch({ type: AUTH_TOKEN, token });
  } else {
    dispatch({ type: AUTH_TOKEN_USER, user: null });
    dispatch({ type: AUTH_TOKEN, token: null });
  }
};


export const createAuthToken = ({ scope, expiresIn } = {}) => async (dispatch, getState) => {
  const { authToken } = getState();
  if (!authToken) {
    throw new Error('Missing authToken');
  }
  const { token } = await callWebApi('POST', API_AUTH_TOKEN_URL, { scope, expiresIn }, authToken);
  return token;
};


export const refreshAuthToken = () => async (dispatch) => {
  const token = await dispatch(createAuthToken());
  await dispatch(setAuthToken(token));
  return token;
};


export const extractAuthTokenFromQuery = (name = 'authToken') => ((dispatch) => {
  let token = null;
  const parts = window.location.search
    .substring(1).split('&')
    .map(e => e.split('='))
    .find(e => e[0] === name);
  if (parts && parts.length > 1) {
    [, token] = parts;
  }
  if (token) {
    dispatch(setAuthToken(token));
  }
  return token;
});


export const loginWithEmailPassphrase = (email, passphrase, ssn) => async (dispatch) => {
  try {
    let res = await fetch(API_AUTH_LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, passphrase, ssn }),
    });
    if (!res.ok) {
      const { message } = (await res.json()).error;
      throw new Error(message);
    }
    const resContentType = res.headers.get('content-type');
    if (resContentType && resContentType.indexOf('application/json') === 0) {
      res = await res.json();
    } else {
      res = await res.text();
    }
    await dispatch(setAuthToken(res.token));
    return res;
  } catch (err) {
    throw err;
  }
};


export const requestResetCredentials = email => async () => (
  callWebApi('POST', API_RESET_CREDENTIALS_REQUEST, { email })
);

export const updateCredentials = (email, passphrase, token) => async () => {
  const url = replacePlaceholders(API_UPDATE_CREDENTIALS, { token });
  return callWebApi('PUT', url, { email, passphrase, token });
};

const transfromSnapQuote = (data) => {
  const dataSymbolPairs = data && data.map(rcd => (
    [rcd.symbol, {
      ...rcd,
      isFetching: false,
    }]));

  return (fromPairs(dataSymbolPairs));
};

export const fetchQuotesSnapQuote = (symbols, force = false) => async (dispatch, getState) => {
  const url = replacePlaceholders(API_QUOTES_SNAPQUOTES_URL, { symbols });

  const fn = createFetchActionCreator(url, 'quotedata', { symbols }, {
    transformData: transfromSnapQuote,
    force,
  })();
  return fn(dispatch, getState);
};


export const fetchQuotesEnhancedSnapQuote = symbols => async (dispatch, getState) => {
  const url = replacePlaceholders(API_QUOTES_ENHANCEDSNAPQUOTES_URL, { symbols });

  const transformData = (data) => {
    const dataSymbolPairs = data.map(rcd => (
      [rcd.symbolstring, {
        ...rcd,
        isFetching: false,
      }]));

    return (fromPairs(dataSymbolPairs));
  };

  const fn = createFetchActionCreator(url, 'enhancedquotedata', { symbols }, { transformData })();
  return fn(dispatch, getState);
};


export const fetchQuotesChartData = (symbols, startDate, interval) =>
  async (dispatch, getState) => {
    const url = replacePlaceholders(API_QUOTES_CHARTDATA_URL, { symbols, startDate, interval });

    const transformData = (data) => {
      const historyData = get(data, 'history', []);
      const reducedHistory = [];

      // Reduce size of historyData
      historyData.forEach((historySet) => {
        if (historySet.eoddata && historySet.eoddata.length > 365) {
          let nextDate = new Moment();

          reducedHistory.push({
            ...historySet,
            eoddata: historySet.eoddata.filter((histRcd) => {
              if (Moment(nextDate).isSameOrAfter(histRcd.date, 'day')) {
                nextDate = Moment(nextDate).subtract(7, 'days');
                return true;
              }
              return false;
            }),
          });
        } else {
          reducedHistory.push(historySet);
        }
      });

      const dataSymbolPairs = get(data, 'intraday', []).map((intra) => {
        // Find the matching history data to include with the symbol data pair
        const matchingHistory = reducedHistory.filter(histRcd =>
          histRcd.symbolstring === intra.symbolstring)[0] || {};

        return ([intra.symbolstring, {
          intraday: intra.interval,
          eoddata: matchingHistory.eoddata,
          isFetching: false,
        }]);
      });

      return (fromPairs(dataSymbolPairs));
    };

    const fn = createFetchActionCreator(url, 'results', { symbols }, { transformData })();
    return fn(dispatch, getState);
  };

const transformQuotesPrivate = (data) => {
  const dataSymbolPairs = data.map(rcd => (
    [rcd.symbol, {
      ...rcd,
      isFetching: false,
    }]));

  return (fromPairs(dataSymbolPairs));
};

export const fetchQuotesPrivate = symbols => async (dispatch, getState) => {
  const url = replacePlaceholders(API_QUOTES_PRIVATE_URL, { symbols });
  const fn = createFetchActionCreator(url, 'quotedata', { symbols }, {
    transformData: transformQuotesPrivate,
  })();
  return fn(dispatch, getState);
};


export const fetchAccountPositionsWithAccountValue = accountNumber => async (
  dispatch,
  getState,
) => {
  const state = getState();
  if (get(state, 'accountValue.isFetching')) {
    return null;
  }

  dispatch({ type: ACCOUNT_VALUE_REQUESTED });
  let quotes; // response from quotemedia for non private securities
  let privquotes; // quotemedia for private securities
  let purchaseTot = 0; // Total purchase price for all securities

  const positions = await dispatch(fetchAccountPositions(accountNumber));
  const nonPrivPos = positions.filter(isTradablePosition);
  const privPos = positions ? positions.filter(pos => !nonPrivPos.includes(pos)) : [];

  const symbols = nonPrivPos
    .map(pos => pos.security.ticker);

  if (symbols.length > 0) {
    quotes = transfromSnapQuote(await dispatch(fetchQuotesSnapQuote(join(symbols, ','))));
  }

  const privateSymbols = privPos
    .map(pos => pos.security.ticker);
  if (privateSymbols.length > 0) {
    privquotes = await dispatch(fetchQuotesPrivate(join(privateSymbols, ',')));
  }

  const preppedPos = prepJSONforSecuritiesGrid(nonPrivPos, quotes);
  const preppedPrivPos = prepJSONforPrivGrid(
    privPos,
    transformQuotesPrivate(privquotes || []),
    true,
  );

  const accountValue = preppedPos.reduce((total, pos) => {
    purchaseTot += pos[4] ? pos[4] : 0; // cost basis
    return pos[5] ? total + pos[5] : total + 0; // quote price * share quantity
  }, 0);

  const privAccountValue = preppedPrivPos.reduce((total, pos) => {
    purchaseTot += pos[5] ? pos[5] : 0; // purchase price * share quantity
    return pos[4] ? total + pos[4] : total + 0; // current price * share quantity
  }, 0);

  const percentChange = purchaseTot > 0 ? (accountValue - purchaseTot) / purchaseTot : undefined;
  const result = {
    accountValue: accountValue + privAccountValue,
    percentChange,
  };

  dispatch({
    type: ACCOUNT_VALUE_COMPLETED,
    accountValue: result,
  });
  return result;
};

export const fetchAccountData = accountNumber => (dispatch) => {
  Promise.all([
    fetchAccountCashBalanceWithOfferingOrders,
    fetchAccountPositionsWithAccountValue,
    fetchAccountPerformanceRawData,
  ].map(fn => dispatch(fn(accountNumber))));
};


export const signUpWithEmailPassphrase = (email, passphrase) => (async (dispatch, getState) => {
  const { queryString } = getState();
  const payload = {
    email,
    passphrase,
  };
  if (queryString && queryString.value) {
    payload['x-attribution'] = { queryString };
  }
  dispatch({ type: AUTH_CREDENTIALS_CREATING });
  const res = await callWebApi('POST', API_AUTH_CREDENTIALS_URL, payload);
  dispatch({ type: AUTH_CREDENTIALS_CREATED });
  dispatch(setAuthToken(res.token));
  return res.token;
});


export const signUpPersonalInfo = personalInfo => (async dispatch => (
  dispatch({ type: SIGN_UP_PERSONAL_INFO, personalInfo })
));


export const signUpInvestmentInfo = investmentInfo => (async dispatch => (
  dispatch({ type: SIGN_UP_INVESTMENT_INFO, investmentInfo })
));


export const signUpPlaidInfo = plaidInfo => (async dispatch => (
  dispatch({ type: SIGN_UP_PLAID_INFO, plaidInfo })
));

export const signUpSignature = (signatureImage, signatureImageEncoding) => (async dispatch => (
  dispatch({ type: SIGN_UP_SIGNATURE, signatureImage, signatureImageEncoding })
));


export const signUp = () => async (dispatch, getState) => {
  dispatch({ type: SIGN_UP_PROCESSING });

  const {
    signUpInfo,
    rrSignupForms,
    authToken,
    queryString,
  } = getState();

  const {
    plaidInfo,
    customerAgreementSignatureData,
    customerAgreementSignatureImageEncoding,
    backupWithholding,
  } = signUpInfo;

  const {
    personalInfo,
    investmentInfo,
  } = rrSignupForms;

  let updatedPI = {
    ...personalInfo,
  };

  if (personalInfo.identity && personalInfo.identity.dateOfBirth) {
    updatedPI = {
      ...updatedPI,
      identity: {
        ...personalInfo.identity,
        dateOfBirth: Moment(personalInfo.identity.dateOfBirth, 'MM/DD/YYYY').toISOString(),
      },
    };
  }

  const signUpRequestPayload = {
    ...updatedPI,
    ...investmentInfo,
    plaidInfo,
    customerAgreementSignatureData,
    customerAgreementSignatureImageEncoding,
    backupWithholding,
  };

  if (queryString && queryString.value) {
    signUpRequestPayload['x-attribution'] = { queryString };
  }

  debug('Creating user: %o', signUpRequestPayload);

  const res = await callWebApi('POST', API_USERS_URL, signUpRequestPayload, authToken);

  // Sign up should return a new auth token
  if (!res.token) {
    throw new Error('Sign Up API response missing token.');
  }

  debug('Sign Up returend a token of length %s', res.token.length);
  dispatch({ type: SIGN_UP_COMPLETED, response: res });
  await dispatch(setAuthToken(res.token));
  return res.token;
};


export const fetchPlaidLinkSettings = () => (async (dispatch, getState) => {
  const { authToken, authTokenUser } = getState();
  const url = replacePlaceholders(API_PLAID_LINK_SETTINGS_URL, { userId: authTokenUser.id });
  const res = await callWebApi('GET', url, null, authToken);
  return res.plaidLinkSettings;
});

export const transferFundsInfo = transferInfo => (async dispatch => (
  dispatch({ type: TRANSFER_FUNDS_INFO, transferInfo })
));

export const transferFundsDeposit = (accountNumber, amount, banklinkOid) =>
  (async (dispatch, getState) => {
    const { authToken } = getState();
    dispatch({ type: TRANSFER_FUNDS_DEPOSIT_PROCESSING });
    const url = replacePlaceholders(API_ACCOUNT_DEPOSITS_URL, { accountNumber });
    const res = await callWebApi('POST', url, { amount, banklinkOid }, authToken);
    dispatch({ type: TRANSFER_FUNDS_COMPLETED, eftOid: res.eftOid, requestDate: res.requestDate });
    return res.eftOid;
  });

export const transferFundsWithdrawal = (accountNumber, amount, banklinkOid) =>
  (async (dispatch, getState) => {
    const { authToken } = getState();
    dispatch({ type: TRANSFER_FUNDS_WITHDRAWAL_PROCESSING });
    const url = replacePlaceholders(API_ACCOUNT_WITHDRAWALS_URL, { accountNumber });
    const res = await callWebApi('POST', url, { amount, banklinkOid }, authToken);
    dispatch({ type: TRANSFER_FUNDS_COMPLETED, eftOid: res.eftOid, requestDate: res.requestDate });
    return res.eftOid;
  });

export const cancelTransfer = (accountNumber, oid, transferType) => async (dispatch, getState) => {
  const { authToken } = getState();
  let url;
  let status = 'C';
  switch (transferType) {
    case 'deposit': {
      url = replacePlaceholders(
        `${API_ACCOUNT_DEPOSITS_URL}/{oid}`,
        { accountNumber, oid },
      );
      break;
    }
    case 'withdrawal': {
      url = replacePlaceholders(
        `${API_ACCOUNT_WITHDRAWALS_URL}/{oid}`,
        { accountNumber, oid },
      );
      status = 'CNLD';
      break;
    }
    default:
      break;
  }

  if (!url) {
    throw new Error('Invalid or missing transfer type.');
  }
  dispatch({ type: TRANSFER_CANCELED, id: oid });
  await callWebApi('PUT', url, { status }, authToken);
};

export const fetchTransferWireInstructions = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_ACCOUNT_INSTRUCTIONS_WIRE_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'instructions', { accountNumber }, true)();
  return fn(dispatch, getState);
};
export const fetchTransferCheckInstructions = accountNumber => async (dispatch, getState) => {
  const url = replacePlaceholders(API_ACCOUNT_INSTRUCTIONS_CHECK_URL, { accountNumber });
  const fn = createFetchActionCreator(url, 'instructions', { accountNumber }, true)();
  return fn(dispatch, getState);
};

export const tradeSecurityInfo = tradeInfo => (async dispatch => (
  dispatch({ type: TRADE_SECURITY_INFO, tradeInfo })
));

export const tradeSecurityTransferInfo = transferInfo => (async dispatch => (
  dispatch({ type: TRADE_SECURITY_TRANSFER_INFO, transferInfo })
));

export const verifyEmailAddress = token => (dispatch, getState) => {
  const { authToken } = getState();
  const url = API_VERIFY_EMAIL_URL;
  return callWebApi('POST', url, { token }, authToken);
};


export const checkServiceStatus = ({
  retryIfFailed = false,
} = {}) => async (dispatch, getState) => {
  const { serviceStatus } = getState();
  if (serviceStatus && serviceStatus.isChecking) {
    return;
  }

  dispatch({ type: SERVICE_STATUS_CHECK_REQUESTED });
  try {
    await callWebApi('GET', API_BASE_URL);
    dispatch({
      type: SERVICE_STATUS_CHECK_SUCCEED,
      payload: {
        status: 'ok',
        checkedAt: new Date(),
      },
    });
  } catch (error) {
    dispatch({
      type: SERVICE_STATUS_CHECK_FAILED,
      payload: {
        status: 'error',
        checkedAt: new Date(),
        message: `Could not connect to API server ${API_BASE_URL}`,
        error,
      },
    });
    if (retryIfFailed) {
      // Retry in 2^errorCount * 100 milliseconds
      // or 60 seconds whichever is lower
      const { errorCount = 0 } = (serviceStatus || {});
      const maxWaitInMS = 60 * 1000;
      const waitInMS = Math.min((2 ** errorCount) * 100, maxWaitInMS);
      setTimeout(() => dispatch(checkServiceStatus({ retryIfFailed })), waitInMS);
    }
  }
};


export const checkAuthToken = () => (dispatch, getState) => {
  const { authToken } = getState();
  if (authToken) {
    const payload = jwt.decode(authToken);
    if (new Date() > new Date(payload.exp * 1000)) {
      dispatch(logout());
    }
  }
};


export const checkStatus = ({ retryIfFailed = false } = {}) => async (dispatch) => {
  dispatch({ type: 'CHECK_STATUS/STARTED' });
  await dispatch(checkServiceStatus({ retryIfFailed }));
  await dispatch(checkAuthToken());
  dispatch({ type: 'CHECK_STATUS/COMPLETED' });
};


export const changePassphrase = (
  currentPassphrase,
  newPassphrase,
) => async (dispatch, getState) => {
  dispatch({ type: 'CHANGE_PASSPHRASE/REQUESTED' });
  const { authToken, authTokenUser } = getState();
  const url = replacePlaceholders(API_USER_PASSPHRASE_URL, { userId: authTokenUser.id });
  await callWebApi('PUT', url, { currentPassphrase, newPassphrase }, authToken);
  dispatch({ type: 'CHANGE_PASSPHRASE/SUCCEED' });
};


export const navigate = to => (async dispatch => dispatch(push(to)));


export const setQueryString = queryString => ({
  type: 'QUERY_STRING',
  payload: {
    value: queryString,
    setAt: new Date(),
  },

});

export const toggleHistoryFilter = code => (dispatch) => {
  dispatch({ type: HISTORY_FILTER_TOGGLE, payload: { code } });
};

export const resetHistoryFilter = () => (dispatch) => {
  dispatch({ type: HISTORY_FILTER_RESET, payload: {} });
};



// WEBPACK FOOTER //
// ./src/actions/index.js